import { action, computed, observable } from 'mobx';
import { v4 as uuid } from 'uuid';

import { Address, createBillingAddress } from 'src/apis/billingAPI';
import { PlaceDetails, PlacePrediction } from 'src/apis/googleAPI';
import { VALIDATION_ERRORS } from 'src/util/isValidUser';
import UserModel from './UserModel';

let billingModelInstance: BillingModel;

export default class BillingModel {
  @observable street?: string;
  @observable city: string;
  @observable state: string = '';
  @observable country: string = '';
  @observable zipCode: string = '';
  @observable countryCode?: string;
  @observable prediction?: PlacePrediction;
  googleSessionToken = uuid();

  @action
  regenerateGoogleSessionToken() {
    this.googleSessionToken = uuid();
  }
  static getInstance(): BillingModel {
    if (!billingModelInstance) {
      billingModelInstance = new BillingModel();
    }
    return billingModelInstance;
  }

  @action
  reset() {
    this.street = undefined;
    this.city = '';
    this.state = '';
    this.country = '';
    this.zipCode = '';
    this.countryCode = undefined;
    this.prediction = undefined;
  }

  @action
  update(prediction: PlacePrediction | null, place: PlaceDetails | null) {
    if (!place || !prediction) {
      this.reset();
      return;
    }

    // Only accept valid predictions with place_id (from Google Places API)
    if (!place.place_id || !place.address_components || place.address_components.length === 0) {
      this.reset();
      return;
    }

    this.prediction = prediction;

    const locality = place.address_components.find((component) =>
      component.types.includes('locality')
    )?.long_name;
    const adminLevel2 = place.address_components.find((component) =>
      component.types.includes('administrative_area_level_2')
    )?.long_name;
    this.city = locality || adminLevel2 || '';

    const countryTerm = place.address_components.find((component) =>
      component.types.includes('country')
    );
    this.country = countryTerm?.long_name || '';
    this.countryCode = countryTerm?.short_name;

    this.street = prediction.structured_formatting.main_text;
    this.state =
      place.address_components.find((component) =>
        component.types.includes('administrative_area_level_1')
      )?.long_name || '';
    this.zipCode =
      place.address_components.find((component) => component.types.includes('postal_code'))
        ?.long_name || '';
  }

  @computed
  get address(): Address {
    return {
      street: this.street,
      city: this.city,
      state: this.state,
      country: this.country,
      zip_code: this.zipCode,
      country_code: this.countryCode
    };
  }

  @action
  async save(source: string, user: UserModel) {
    if (!this.isValid.validity) {
      user.trackError(
        VALIDATION_ERRORS.INVALID_ADDRESS,
        new Error(`Failed validation: ${this.isValid.error}`)
      );
    }
    return createBillingAddress({ ...this.address, source });
  }

  @computed
  get isValid(): { validity: true; error: null } | { validity: false; error: string } {
    // Ensure we have a valid prediction from Google Places API
    if (!this.prediction || !this.prediction.place_id) {
      return {
        validity: false,
        error: VALIDATION_ERRORS.INVALID_ADDRESS
      };
    }

    if (this.state.trim() === '') {
      return {
        validity: false,
        error: VALIDATION_ERRORS.INVALID_STATE
      };
    }
    if (this.country.trim() === '') {
      return {
        validity: false,
        error: VALIDATION_ERRORS.INVALID_COUNTRY
      };
    }
    if (this.zipCode.trim() === '') {
      return {
        validity: false,
        error: VALIDATION_ERRORS.INVALID_ZIP_CODE
      };
    }
    return {
      validity: true,
      error: null
    };
  }
}
